import React, { useEffect, useMemo, useState } from 'react';

const LANDING_PAGES = [
  { label: 'Smart Quote Engine', url: 'https://nexoraxdigital.co.uk/smart-quote', purpose: 'Best for TikTok website traffic and lead capture.' },
  { label: 'QR Campaign Generator', url: 'https://nexoraxdigital.co.uk/qr-campaign', purpose: 'Best for QR campaign offer videos.' },
  { label: 'Showcase / Demo Gallery', url: 'https://nexoraxdigital.co.uk/showcase', purpose: 'Best for showing what NexoraX can build.' },
  { label: 'Client Preview Portal', url: 'https://nexoraxdigital.co.uk/client-preview', purpose: 'Best for existing client follow-up.' },
  { label: 'Remote Workplace Demo', url: 'https://nexoraxdigital.co.uk/remote-workplace', purpose: 'Best for showing remote team workflow.' }
];

const OBJECTIVES = [
  'Website visits',
  'Lead generation',
  'Messages',
  'Reach',
  'Traffic test',
  'Conversion test'
];

const STATUS_OPTIONS = [
  'Draft',
  'Ready for Review',
  'Ready to Launch',
  'Live',
  'Paused',
  'Completed',
  'Needs Changes'
];

const VIDEO_TYPES = [
  'NexoraX Promo Video',
  'Smart Quote Explainer',
  'QR Campaign Demo',
  'Showcase / Demo Gallery',
  'Remote Workplace Training',
  'Client Preview Portal',
  'Custom TikTok Video'
];

type CampaignForm = {
  campaignName: string;
  platform: string;
  objective: string;
  landingPage: string;
  videoType: string;
  videoLink: string;
  caption: string;
  hashtags: string;
  audience: string;
  dailyBudget: string;
  totalBudget: string;
  startDate: string;
  endDate: string;
  owner: string;
  status: string;
  notes: string;
  spend: string;
  impressions: string;
  clicks: string;
  leads: string;
};

const initialForm: CampaignForm = {
  campaignName: 'TikTok Smart Quote Traffic Test',
  platform: 'TikTok Ads Manager',
  objective: 'Website visits',
  landingPage: 'https://nexoraxdigital.co.uk/smart-quote',
  videoType: 'Smart Quote Explainer',
  videoLink: '',
  caption: 'Build the right digital system for your business. Try the NexoraX Smart Quote Engine and get a digital direction in minutes.',
  hashtags: '#NexoraX #DigitalMarketing #SmallBusinessUK #WebsiteDesign #AIForBusiness #TikTokAds',
  audience: 'Local UK businesses, trades, restaurants, salons, startups and service businesses.',
  dailyBudget: '5',
  totalBudget: '15',
  startDate: '',
  endDate: '',
  owner: 'Mahmood',
  status: 'Draft',
  notes: 'Tomorrow test: send traffic to Smart Quote. Watch clicks, cost per click and real brief submissions.',
  spend: '0',
  impressions: '0',
  clicks: '0',
  leads: '0'
};

const buildTrackingUrl = (form: CampaignForm) => {
  const url = new URL(form.landingPage || 'https://nexoraxdigital.co.uk/smart-quote');
  url.searchParams.set('utm_source', 'tiktok');
  url.searchParams.set('utm_medium', 'paid_social');
  url.searchParams.set('utm_campaign', form.campaignName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'nexorax_campaign');
  url.searchParams.set('utm_content', form.videoType.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'video');
  return url.toString();
};

const safeNumber = (value: string) => {
  const parsed = Number(String(value || '').replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function NexoraXCampaignLaunchPad() {
  const [form, setForm] = useState<CampaignForm>(initialForm);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const trackingUrl = useMemo(() => buildTrackingUrl(form), [form]);

  const metrics = useMemo(() => {
    const spend = safeNumber(form.spend);
    const impressions = safeNumber(form.impressions);
    const clicks = safeNumber(form.clicks);
    const leads = safeNumber(form.leads);

    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    const cpc = clicks > 0 ? spend / clicks : 0;
    const cpl = leads > 0 ? spend / leads : 0;
    const leadRate = clicks > 0 ? (leads / clicks) * 100 : 0;

    return {
      ctr: ctr.toFixed(2),
      cpc: cpc.toFixed(2),
      cpl: cpl.toFixed(2),
      leadRate: leadRate.toFixed(2)
    };
  }, [form.spend, form.impressions, form.clicks, form.leads]);

  const selectedLanding = LANDING_PAGES.find((page) => page.url === form.landingPage);

  const loadCampaigns = async () => {
    try {
      const response = await fetch('/api/campaign-launches');
      const data = await response.json();
      if (data.success) setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
    } catch {
      setNotice('Could not load saved campaigns.');
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const saveCampaign = async () => {
    if (!form.campaignName.trim()) {
      setNotice('Add a campaign name before saving.');
      return;
    }

    setLoading(true);
    setNotice('');

    try {
      const response = await fetch('/api/campaign-launches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, trackingUrl, metrics })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Save failed.');

      setNotice(`Campaign saved. Reference: ${data.campaign.reference}`);
      await loadCampaigns();
    } catch {
      setNotice('Campaign could not be saved.');
    } finally {
      setLoading(false);
    }
  };

  const updateCampaignStatus = async (id: string, status: string) => {
    try {
      const response = await fetch(`/api/campaign-launches/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Update failed.');
      setNotice(`Campaign status updated: ${status}`);
      await loadCampaigns();
    } catch {
      setNotice('Could not update campaign status.');
    }
  };

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice(`${label} copied.`);
      setTimeout(() => setNotice(''), 2500);
    } catch {
      setNotice('Clipboard blocked. Select and copy manually.');
    }
  };

  const launchChecklist = [
    'Video file uploaded or Canva/TikTok asset link saved',
    'Landing page selected and tracking URL copied',
    'Caption and hashtags checked',
    'Daily budget and total test budget set',
    'TikTok Ads Manager objective selected',
    'Audience chosen',
    'No payment or bank details pasted into NexoraX',
    'Campaign marked Ready to Launch before spend starts'
  ];

  return (
    <main className="min-h-screen overflow-hidden bg-[#02030a] text-white">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_12%_10%,rgba(0,240,255,0.18),transparent_35%),radial-gradient(circle_at_88%_18%,rgba(168,85,247,0.22),transparent_35%),linear-gradient(180deg,#02030a,#050014_55%,#02030a)]" />
      <div className="pointer-events-none fixed inset-0 opacity-30 [background-image:linear-gradient(rgba(0,240,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.05)_1px,transparent_1px)] [background-size:42px_42px]" />

      <section className="relative mx-auto max-w-7xl px-5 py-10 md:py-16">
        <div className="rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_60px_rgba(0,240,255,0.12)] backdrop-blur-xl md:p-10">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.45em] text-cyan-300">
                NexoraX Campaign Launch Pad
              </p>
              <h1 className="mt-4 max-w-4xl text-4xl font-black leading-tight md:text-6xl">
                Prepare TikTok campaigns with budget, tracking and lead flow.
              </h1>
              <p className="mt-5 max-w-3xl text-slate-300">
                Plan the video, caption, landing page, UTM tracking link, daily budget and results before sending paid traffic to NexoraX.
              </p>
            </div>

            <div className="rounded-3xl border border-purple-500/30 bg-purple-950/20 p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-purple-300">Tomorrow Test</p>
              <p className="mt-2 text-2xl font-black text-cyan-300">TikTok → Smart Quote</p>
              <p className="mt-1 text-xs text-slate-400">Track spend, clicks and leads</p>
            </div>
          </div>
        </div>

        {notice && (
          <div className="mt-6 rounded-2xl border border-purple-500/30 bg-purple-950/20 p-4 text-sm text-purple-100">
            {notice}
          </div>
        )}

        <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <section className="rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_45px_rgba(0,240,255,0.10)] backdrop-blur-xl">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
              Campaign setup
            </p>
            <h2 className="mt-2 text-2xl font-black">Build tomorrow’s TikTok test</h2>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <input
                value={form.campaignName}
                onChange={(e) => setForm({ ...form, campaignName: e.target.value })}
                placeholder="Campaign name"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400 md:col-span-2"
              />

              <select
                value={form.platform}
                onChange={(e) => setForm({ ...form, platform: e.target.value })}
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              >
                <option>TikTok Ads Manager</option>
                <option>TikTok Promote</option>
                <option>Instagram / Facebook</option>
                <option>Organic TikTok</option>
              </select>

              <select
                value={form.objective}
                onChange={(e) => setForm({ ...form, objective: e.target.value })}
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              >
                {OBJECTIVES.map((item) => <option key={item}>{item}</option>)}
              </select>

              <select
                value={form.landingPage}
                onChange={(e) => setForm({ ...form, landingPage: e.target.value })}
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400 md:col-span-2"
              >
                {LANDING_PAGES.map((page) => <option key={page.url} value={page.url}>{page.label} — {page.url}</option>)}
              </select>

              <select
                value={form.videoType}
                onChange={(e) => setForm({ ...form, videoType: e.target.value })}
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              >
                {VIDEO_TYPES.map((item) => <option key={item}>{item}</option>)}
              </select>

              <input
                value={form.videoLink}
                onChange={(e) => setForm({ ...form, videoLink: e.target.value })}
                placeholder="Video link / Canva link / Drive link"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <textarea
                value={form.caption}
                onChange={(e) => setForm({ ...form, caption: e.target.value })}
                placeholder="TikTok caption"
                className="min-h-[110px] rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-purple-400 md:col-span-2"
              />

              <textarea
                value={form.hashtags}
                onChange={(e) => setForm({ ...form, hashtags: e.target.value })}
                placeholder="Hashtags"
                className="min-h-[80px] rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-purple-400 md:col-span-2"
              />

              <textarea
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
                placeholder="Audience notes"
                className="min-h-[80px] rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400 md:col-span-2"
              />

              <input
                value={form.dailyBudget}
                onChange={(e) => setForm({ ...form, dailyBudget: e.target.value })}
                placeholder="Daily budget £"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <input
                value={form.totalBudget}
                onChange={(e) => setForm({ ...form, totalBudget: e.target.value })}
                placeholder="Total test budget £"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              >
                {STATUS_OPTIONS.map((item) => <option key={item}>{item}</option>)}
              </select>

              <input
                value={form.owner}
                onChange={(e) => setForm({ ...form, owner: e.target.value })}
                placeholder="Owner"
                className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
              />

              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Launch notes"
                className="min-h-[90px] rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-purple-400 md:col-span-2"
              />
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                disabled={loading}
                onClick={saveCampaign}
                className="rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/50 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Launch Campaign'}
              </button>

              <button
                type="button"
                onClick={() => copyText(trackingUrl, 'Tracking URL')}
                className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
              >
                Copy Tracking URL
              </button>

              <a
                href={trackingUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-pink-500/40 bg-pink-950/30 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-pink-100 hover:bg-pink-900/40"
              >
                Test Landing Page
              </a>
            </div>
          </section>

          <section className="space-y-6">
            <div className="rounded-[2rem] border border-purple-500/25 bg-black/60 p-6 shadow-[0_0_45px_rgba(168,85,247,0.10)] backdrop-blur-xl">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-300">
                Tracking command centre
              </p>
              <h2 className="mt-2 text-2xl font-black">Budget, link and KPI tracking</h2>

              <div className="mt-5 rounded-2xl border border-cyan-500/20 bg-cyan-950/10 p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">Tracking URL</p>
                <input
                  readOnly
                  value={trackingUrl}
                  onFocus={(e) => e.target.select()}
                  className="mt-3 w-full rounded-xl border border-zinc-800 bg-black px-4 py-3 text-xs text-cyan-100 outline-none"
                />
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-4">
                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Daily Budget</p>
                  <p className="mt-2 text-2xl font-black text-cyan-300">£{form.dailyBudget || '0'}</p>
                </div>

                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Total Budget</p>
                  <p className="mt-2 text-2xl font-black text-purple-300">£{form.totalBudget || '0'}</p>
                </div>

                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Objective</p>
                  <p className="mt-2 text-sm font-bold text-white">{form.objective}</p>
                </div>

                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Status</p>
                  <p className="mt-2 text-sm font-bold text-cyan-200">{form.status}</p>
                </div>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-4">
                <input
                  value={form.spend}
                  onChange={(e) => setForm({ ...form, spend: e.target.value })}
                  placeholder="Spend £"
                  className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                />
                <input
                  value={form.impressions}
                  onChange={(e) => setForm({ ...form, impressions: e.target.value })}
                  placeholder="Impressions"
                  className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                />
                <input
                  value={form.clicks}
                  onChange={(e) => setForm({ ...form, clicks: e.target.value })}
                  placeholder="Clicks"
                  className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                />
                <input
                  value={form.leads}
                  onChange={(e) => setForm({ ...form, leads: e.target.value })}
                  placeholder="Leads"
                  className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-cyan-400"
                />
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-4">
                <div className="rounded-2xl border border-cyan-500/20 bg-cyan-950/10 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">CTR</p>
                  <p className="mt-2 text-2xl font-black text-cyan-300">{metrics.ctr}%</p>
                </div>
                <div className="rounded-2xl border border-purple-500/20 bg-purple-950/10 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">CPC</p>
                  <p className="mt-2 text-2xl font-black text-purple-300">£{metrics.cpc}</p>
                </div>
                <div className="rounded-2xl border border-pink-500/20 bg-pink-950/10 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Cost / Lead</p>
                  <p className="mt-2 text-2xl font-black text-pink-300">£{metrics.cpl}</p>
                </div>
                <div className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Lead Rate</p>
                  <p className="mt-2 text-2xl font-black text-white">{metrics.leadRate}%</p>
                </div>
              </div>
            </div>

            <div className="rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 backdrop-blur-xl">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                Launch checklist
              </p>
              <div className="mt-4 grid gap-3">
                {launchChecklist.map((item, index) => (
                  <div key={item} className="rounded-2xl border border-zinc-800 bg-black/70 p-4 text-sm text-slate-200">
                    <span className="mr-2 font-mono text-cyan-300">{index + 1}.</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-purple-500/25 bg-black/60 p-6 backdrop-blur-xl">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-300">
                  Copy campaign pack
                </p>
                <button
                  type="button"
                  onClick={() => copyText(
                    `Campaign: ${form.campaignName}\nPlatform: ${form.platform}\nObjective: ${form.objective}\nLanding Page: ${form.landingPage}\nTracking URL: ${trackingUrl}\nVideo Type: ${form.videoType}\nVideo Link: ${form.videoLink}\nCaption: ${form.caption}\nHashtags: ${form.hashtags}\nAudience: ${form.audience}\nDaily Budget: £${form.dailyBudget}\nTotal Budget: £${form.totalBudget}\nStatus: ${form.status}\nNotes: ${form.notes}`,
                    'Campaign pack'
                  )}
                  className="rounded-xl border border-purple-500/40 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100 hover:bg-purple-950/40"
                >
                  Copy Campaign Pack
                </button>
              </div>

              <div className="mt-4 rounded-2xl border border-zinc-800 bg-black/70 p-5 text-sm leading-relaxed text-slate-300">
                <p><strong className="text-white">Landing:</strong> {selectedLanding?.label || form.landingPage}</p>
                <p className="mt-2"><strong className="text-white">Purpose:</strong> {selectedLanding?.purpose || 'Custom landing page.'}</p>
                <p className="mt-2"><strong className="text-white">Caption:</strong> {form.caption}</p>
                <p className="mt-2"><strong className="text-white">Hashtags:</strong> {form.hashtags}</p>
              </div>
            </div>
          </section>
        </div>

        <section className="mt-8 rounded-[2rem] border border-cyan-500/25 bg-black/60 p-6 shadow-[0_0_45px_rgba(0,240,255,0.10)] backdrop-blur-xl">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-300">
                Saved launch campaigns
              </p>
              <h2 className="mt-2 text-2xl font-black">Campaign tracker</h2>
            </div>

            <button
              type="button"
              onClick={loadCampaigns}
              className="rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-cyan-100 hover:bg-cyan-900/40"
            >
              Reload Campaigns
            </button>
          </div>

          {campaigns.length === 0 ? (
            <div className="mt-5 rounded-2xl border border-zinc-800 bg-black/70 p-5 text-sm text-slate-400">
              No launch campaigns saved yet.
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              {campaigns.slice(0, 10).map((campaign) => (
                <div key={campaign.id} className="rounded-3xl border border-zinc-800 bg-black/70 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-black text-cyan-200">{campaign.campaignName}</h3>
                      <p className="mt-1 text-xs text-slate-400">
                        Ref: {campaign.reference} • {campaign.platform} • {campaign.objective} • {campaign.createdAt ? new Date(campaign.createdAt).toLocaleString() : ''}
                      </p>
                    </div>
                    <span className="rounded-full border border-purple-500/40 bg-purple-950/30 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-purple-100">
                      {campaign.status}
                    </span>
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-4">
                    <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Daily / Total</p>
                      <p className="mt-2 text-sm text-white">£{campaign.dailyBudget} / £{campaign.totalBudget}</p>
                    </div>
                    <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">CTR</p>
                      <p className="mt-2 text-sm text-cyan-200">{campaign.metrics?.ctr || '0.00'}%</p>
                    </div>
                    <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">CPC</p>
                      <p className="mt-2 text-sm text-purple-200">£{campaign.metrics?.cpc || '0.00'}</p>
                    </div>
                    <div className="rounded-2xl border border-zinc-800 bg-black p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Leads</p>
                      <p className="mt-2 text-sm text-white">{campaign.leads || '0'}</p>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl border border-cyan-500/20 bg-cyan-950/10 p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">Tracking URL</p>
                    <p className="mt-2 break-all text-xs text-cyan-100">{campaign.trackingUrl}</p>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map((status) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => updateCampaignStatus(campaign.id, status)}
                        className="rounded-full border border-cyan-500/30 px-3 py-2 text-[11px] text-cyan-100 hover:bg-cyan-950/40"
                      >
                        {status}
                      </button>
                    ))}
                    <a
                      href={campaign.trackingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full border border-purple-500/40 px-3 py-2 text-[11px] text-purple-100 hover:bg-purple-950/40"
                    >
                      Test Link
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </section>
    </main>
  );
}
